<template>
  <div class="cat-proficiency">
    <NavigationBar />

    <div class="proficiency-header">
      <div class="header-title">
        {{ $t('proficiency.title', { user: isParentUser ? $t('common.child') : $t('common.me') }) }}
      </div>

      <SubHeaderTab
        :items="filterSubjects"
        :selectedId="selectedSubjectId"
        @selectedTab="selectedTab"
      />
    </div>

    <template v-if="busy">
      <LoadingBounce />
    </template>

    <template v-else>

      <div class="proficiency-container">

        <div class="chart-container" :style="{ 'background-color': selectedChartBackgroundColor }">
          <div class="chart-description">
            {{ $t('proficiency.chart_title', { user: isParentUser ? $t('common.child') : $t('common.me') }) }}
          </div>
          <CatProficiencyChart
            :chartData="chartData"
            :chartFontColor="chartFontColor"
            :height="160" />
        </div>

        <div class="row align-items-center">
          <CatScore :selectedSubjectId="selectedSubject" 
            :selectedSubjectColor="selectedChartBackgroundColor" />
          <CatSubjectRank :selectedSubjectId="selectedSubject" />
        </div>

        <CatRankPolicy />

        <template v-if="isSelectedSubjectId">
          <div class="domain-container" v-if="filterCatSubjectDomains.length > 0">
            <div class="cl-title">{{ $t('common.domain') }} {{ filterCatSubjectDomains.length }}</div>
            <div class="row">
              <div class="col-lg-4 col-xs-12 col-md-12 px-6 domain" 
                v-for="(value, index) in filterCatSubjectDomains" :key="index"
                @click="goToLearningElement(value.id)">
                <div class="cl-card">
                  <div class="domain-name">{{ value.name }}</div>
                  <div class="domain-state">
                    {{ $t('common.score') }} <span>{{ value.proficiency_score || '-' }}{{ $t('common.score_value') }}</span>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{{ $t('common.rank') }}
                    <template v-if="isGradeTotal(value.rank && value.rank.grade_total)">
                      <span>{{ ratioValue(value.rank && value.rank.grade_position, value.rank && value.rank.grade_total ) || '-' }}%</span>
                    </template>
                    <template v-else>
                      <span :style="{ 'color': '#00C896' }">-%</span> {{ $t('common.standard_before_update') }}
                    </template>
                  </div>
                </div>
              </div>
              <InfiniteLoading @infinite="infiniteHandler" class="w-100" v-if="isFilterCatSubjectDomains">
                <div slot="spinner">
                  <LoadingBounce></LoadingBounce>
                </div>
                <span slot="no-results"></span>
                <span slot="no-more"></span>
              </InfiniteLoading>
            </div>
          </div>
        </template>
      </div>
    </template>
  </div>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';
  import { isEmpty, find } from 'lodash';
  import InfiniteLoading from 'vue-infinite-loading';

  import numbers from '@/mixins/numbers';
  import CatProficiencyChart from '../components/CatProficiencyChart';
  import CatSubjectRank from '../components/CatSubjectRank';
  import CatScore from '../../cat-main/components/CatScore';
  import CatRankPolicy from '../../cat-main/components/CatRankPolicy';

  export default {
    name: 'cat-proficiency',

    data() {
      return {
        selectedSubject: 0,
        selectedChartBackgroundColor: '#00C896',
        chartFontColor: 'rgba(255, 255, 255)',
        busy: false,
        limit: 10,
      };
    },

    components: {
      CatProficiencyChart,
      CatScore,
      CatSubjectRank,
      CatRankPolicy,
      InfiniteLoading,
    },

    mixins: [
      numbers,
    ],

    computed: {
      ...mapGetters({
        filterSubjects: 'getFilterSubjects',
        catSubjectDomains: 'getCatSubjectDomains',
        filterProficiencyRank: 'getFilterProficiencyRank',
        schoolRate: 'getSchoolRate',
        previousSchoolRate: 'getPreviousSchoolRate',
        currentScore: 'getCurrentScore',
        averageScore: 'getAverageScore',
        currentScoreRate: 'getCurrentScoreRate',
        changedSchoolRate: 'getChangedSchoolRate',
        orderProficiencyHistory: 'getOrderProficiencyHistory',
        historyScoresData: 'getHistoryScoresData',
        historyScoreDateLabels: 'getHistoryScoreDateLabels',
        selectedSubjectId: 'getSelectedSubjectId',
        isParentUser: 'isParentUser',
        selectedChildId: 'getSelectedChildId',
        subjectDomainPosition: 'getSubjectDomainPosition',
      }),

      isFilterCatSubjectDomains() {
        return this.filterCatSubjectDomains.length >= 10;
      },

      chartData() {
        return {
          labels: this.historyScoreDateLabels,
          datasets: [
            {
              borderColor: 'rgba(255, 255, 255, 0.0)',
              pointBackgroundColor: '#FFFFFF',
              borderWidth: 1,
              pointBorderColor: this.selectedChartBackgroundColor,
              data: this.historyScoresData,
            },
          ],
        };
      },

      filterCatSubjectDomains() {
        return this.catSubjectDomains && this.catSubjectDomains[this.selectedSubjectId];
      },

      isSelectedSubjectId() {
        return this.selectedSubjectId !== 0;
      },

      filterSubjectDomainPosition() {
        return this.subjectDomainPosition[this.selectedSubjectId];
      },

    },

    methods: {
      ...mapActions([
        'fetchSubjects',
        'fetchCatDomains',
        'fetchChildProficiencyRank',
        'fetchFilterProficiencyRank',
        'fetchChildProficiencyHistory',
        'fetchFilterProficiencyHistory',
        'setSelectedSubjectId',
        'resetDomainProficiencyRank',
        'resetLearningElement',
      ]),

      selectedTab(selected) {
        this.$ga.event({
          eventCategory: 'learning',
          eventAction: 'click.level_subject',
          eventLabel: selected.id,
        });

        this.selectedSubject = selected.id;
        this.selectedChartBackgroundColor = selected.color_code;
        this.setSelectedSubjectId(selected.id);
      },

      changedReateResource(rate) {
        if (rate < 0) {
          return {
            icon: 'ic-info-decrease.svg',
            color: '#448aff',
          };
        }

        return {
          icon: 'ic-info-increase.svg',
          color: '#ff595f',
        };
      },

      ratioValue(value, total) {
        return Number(((value / total) * 100).toFixed(1));
      },

      isGradeTotal(gradeTotal) {
        return gradeTotal >= 10;
      },

      goToLearningElement(domainId) {
        if (this.isParentUser) {
          return this.$router.push({
            name: 'cat-learning-element',
            params: {
              domainId,
            },
            query: {
              child_id: this.selectedChildId,
            },
          });
        }

        return this.$router.push({
          name: 'cat-learning-element',
          params: {
            domainId,
          },
        });
      },

      infiniteHandler($state) {
        const filters = {};
        filters.page = this.filterSubjectDomainPosition;
        filters.per_page = this.limit;
        filters.subject_id = this.selectedSubjectId;

        if (this.isParentUser) {
          filters.child_id = this.selectedChildId;
        }

        this.fetchCatDomains(filters)
          .then((data) => {
            if (data.length > 0) {
              $state.loaded();
            } else {
              $state.complete();
            }
          });
      },

    },

    beforeMount() {
      this.$route.meta.title = this.$t('proficiency.title', { user: this.isParentUser ? this.$t('common.child') : this.$t('common.me') });
      this.busy = true;
      this.resetLearningElement();
      this.resetDomainProficiencyRank();
      const filters = {};
      if (this.isSelectedSubjectId) {
        filters.subject_id = this.selectedSubjectId;
      }

      if (this.isParentUser) {
        filters.child_id = this.selectedChildId;
      }

      const selectedSubject = find(this.filterSubjects, item => (
        item.id === this.selectedSubjectId
      ));

      this.selectedChartBackgroundColor = selectedSubject.color_code;

      Promise.resolve()
        .then(() => {
          if (isEmpty(this.catSubjects)) {
            return this.fetchSubjects();
          }
          return true;
        })
        .then(() => {
          if (this.isSelectedSubjectId) {
            filters.subject_id = this.selectedSubjectId;
          }

          if (this.isParentUser) {
            filters.child_id = this.selectedChildId;
            return this.fetchChildProficiencyHistory(filters);
          }
          return this.fetchFilterProficiencyHistory(filters);
        })
        .then(() => {
          if (this.isParentUser) {
            return this.fetchChildProficiencyRank(filters);
          }

          return this.fetchFilterProficiencyRank(filters);
        })
        .then(() => {
          if (this.isSelectedSubjectId && !this.filterCatSubjectDomains) {
            return this.fetchCatDomains(filters);
          }
          return true;
        })
        .then(() => {
          this.busy = false;
        });
    },

    watch: {
      selectedSubjectId(selected) {
        const filters = {};

        if (selected !== 0) {
          filters.subject_id = selected;
        }

        if (!this.isParentUser && !this.filterCatSubjectDomains) {
          this.busy = true;
          this.fetchCatDomains(filters)
            .then(() => {
              this.busy = false;
            });
        }

        if (this.isParentUser && !this.filterCatSubjectDomains) {
          this.busy = true;
          filters.child_id = this.selectedChildId;
          this.fetchCatDomains(filters)
            .then(() => {
              this.busy = false;
            });
        }
      },
    },

  };
</script>

<style lang="scss" scoped>

  .cat-proficiency {
  
    .proficiency-header {
      height: 100px;
      width: 100%;
      background-color: #ffffff;
      box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.1);
      padding: 24px;

      .header-title {
        max-width: 946px;
        margin: 0 auto;
        font-size: 20px;
        font-weight: bold;
        color: rgba(0, 0, 0, 0.54);
      }

      .subject-container {
        padding: 26px 0px 26px 0px;
        max-width: 946px;
        margin: 0 auto;

        .subject {
          text-align: center;
          display: inline-block;
          padding: 4px 44px 4px 44px;
          font-weight: bold;
          cursor: pointer;
          color: rgba(0, 0, 0, 0.4);
          font-size: 14px;

          &:hover {
            color: #00C896;
          }

          &.active {
            color: #00C896;
            border-bottom: 2px solid #00C896;
          }
        }
      }
    }

    .proficiency-container {
      max-width: 946px;
      margin: 0 auto;

      .chart-container {
        background-color: #03a9f4;
        padding: 16px 36px 18px 36px;
        margin-top: 14px;
        margin-bottom: 14px;
        
        @media (max-width: 768px) {
          padding: 8px 14px 14px 14px;
        }

        .chart-description {
          padding: 14px 0px 28px 0;
          font-size: 16px;
          color: #ffffff;
        }
      }

      .domain-container {
        padding: 28px 0px 0px 0px;

        .domain {
          margin-bottom: 14px;
          cursor: pointer;

          .domain-name {
            font-size: 18px;
            color: rgba(0, 0, 0, 0.87);
            font-weight: 500;
            padding: 16px;
          }

          .domain-state {
            padding: 16px;
            font-size: 16px;
            font-weight: 500;
            color: rgba(0, 0, 0, 0.54);

            span {
              color: #00c896;
            }
          }
        }
      }
    }
  }
</style>


// WEBPACK FOOTER //
// CatProficiency.vue?21b88e8a